import React, { useState, useRef, useEffect } from 'react';
import { BlogAuthor } from 'types/interfaces/blog';
import { useGetUsersQuery } from 'store/services/admin/adminApi';
import { useDebouncedValue } from 'hooks/useDebouncedValue';
import { AvatarWithFlag } from 'components/AvatarWithFlag/AvatarWithFlag';
import './AuthorSelect.css';

interface AuthorSelectProps {
  selectedAuthor: BlogAuthor | null;
  onAuthorChange: (author: BlogAuthor | null) => void;
  readOnly?: boolean;
}

const AuthorSelect: React.FC<AuthorSelectProps> = ({ selectedAuthor, onAuthorChange, readOnly = false }) => {
  const [searchText, setSearchText] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const debouncedSearch = useDebouncedValue(searchText, 300);
  const containerRef = useRef<HTMLDivElement>(null);

  const { data: searchResults } = useGetUsersQuery(
    { page: 1, pageSize: 10, search: debouncedSearch },
    { skip: !debouncedSearch || debouncedSearch.length < 2 || readOnly }
  );

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (user: { id: number; username: string; avatarUrl?: string | null }) => {
    onAuthorChange({
      id: user.id,
      username: user.username,
      avatarUrl: user.avatarUrl || null,
    });
    setSearchText('');
    setIsDropdownOpen(false);
  };

  const handleClear = () => {
    onAuthorChange(null);
    setSearchText('');
  };

  const renderAvatar = (username: string, avatarUrl: string | null, size: number = 28) => {
    return (
      <AvatarWithFlag
        avatarUrl={avatarUrl}
        username={username}
        size={size}
      />
    );
  };

  // Read-only mode for writers
  if (readOnly) {
    return (
      <div className="author-select">
        {selectedAuthor ? (
          <div className="author-select-preview">
            {renderAvatar(selectedAuthor.username, selectedAuthor.avatarUrl)}
            <span className="author-select-preview-name">{selectedAuthor.username}</span>
          </div>
        ) : (
          <span className="author-select-empty">No author assigned</span>
        )}
      </div>
    );
  }

  // Editable mode for admins
  return (
    <div className="author-select" ref={containerRef}>
      {selectedAuthor ? (
        <div className="author-select-selected">
          <div className="author-select-preview">
            {renderAvatar(selectedAuthor.username, selectedAuthor.avatarUrl)}
            <span className="author-select-preview-name">{selectedAuthor.username}</span>
          </div>
          <button
            type="button"
            className="author-select-clear"
            onClick={handleClear}
            title="Remove author"
          >
            &times;
          </button>
        </div>
      ) : (
        <div className="author-select-search-wrapper">
          <input
            type="text"
            className="author-select-input"
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
              setIsDropdownOpen(true);
            }}
            onFocus={() => {
              if (searchText.length >= 2) setIsDropdownOpen(true);
            }}
            placeholder="Search users by username..."
          />
          {isDropdownOpen && searchResults?.users && searchResults.users.length > 0 && (
            <div className="author-select-dropdown">
              {searchResults.users.map((user) => (
                <div
                  key={user.id}
                  className="author-select-option"
                  onClick={() => handleSelect(user)}
                >
                  {renderAvatar(user.username, user.avatarUrl || null, 24)}
                  <span className="author-select-option-name">{user.username}</span>
                </div>
              ))}
            </div>
          )}
          {isDropdownOpen && debouncedSearch.length >= 2 && searchResults?.users?.length === 0 && (
            <div className="author-select-dropdown">
              <div className="author-select-no-results">No users found</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AuthorSelect;
